// "use client";

// Cause Icon
import Arts from "../assets/icons/causes/Arts";
import Development from "../assets/icons/causes/Development";
import Education from "../assets/icons/causes/Education";
import Environment from "../assets/icons/causes/Environment";
import Faith from "../assets/icons/causes/Faith";
import Health from "../assets/icons/causes/Health";
import Media from "../assets/icons/causes/Media";
import Policy from "../assets/icons/causes/Policy";
import Relief from "../assets/icons/causes/Relief";
import Rights from "../assets/icons/causes/Rights";
import Science from "../assets/icons/causes/Science";
import Sports from "../assets/icons/causes/Sports";
import {
  CampaignState as FamilyCampaignStateChoices,
  type FirmType,
} from "../graphql/generated/graphql";
import { applyTerminology } from "./terminology";
import type {
  AddFundsOption,
  getAddFundsMultipleStepOptionsProps,
  LegalDoc,
} from "./types";

export const ROUTE_URL = {
  FIRM: "firm",
  ENTERPRISE: "enterprise",
  COMPANY: "company",
  DONOR: "donor",
  ADVISOR: "advisor",
};

export const FIRM_STATUS = {
  PENDING: "Pending",
  APPROVED: "Approved",
  REJECTED: "Rejected",
};

export const FIRM_SUPPORT_GROUP_TYPE = "firm_support";

export const custodians = [
  "Fidelity",
  "Charles Schwab",
  "TD Ameritrade",
  "E*TRADE",
];

export const AllRolesWithLabels = [
  { label: "Family Member", value: 1 },
  { label: "Advisor", value: 2 },
  { label: "Reviewer", value: 3 },
  { label: "Head Of The Family", value: 4 },
  { label: "Firm Admin", value: 5 },
  { label: "Advisory Ops", value: 6 },
  { label: "Corporate Admin", value: 7 },
  { label: "Self Directed", value: 8 },
  { label: "Enterprise Admin", value: 10 },
];

export enum RelationTypeEnum {
  Child = "Child",
  Spouse = "Spouse",
  Parent = "Parent",
  Sibling = "Sibling",
  Friend = "Friend",
  // Add more as needed
}

export const AllRelationTypes = [
  RelationTypeEnum.Child,
  RelationTypeEnum.Spouse,
  RelationTypeEnum.Parent,
  RelationTypeEnum.Sibling,
  RelationTypeEnum.Friend,
];

export enum MemberTypeEnum {
  HeadOfHousehold = "Head of Household",
  FamilyMember = "Family Member",
  Friend = "Friend",
}

export enum MemberStatusEnum {
  Active = "active",
  Inactive = "inactive",
}

export const familyHierarchy = ["HEAD OF HOUSEHOLD"];

export const charity = ["Charity One", "Charity Two", "Charity Three"];
export const charityFrequency = ["Monthly", "Quarterly", "Annually"];
export const reasonsForGiving = [
  "Thanks for the work you do!",
  "In support of your mission. Thank you!",
  "We are proud to support your impact in the community.",
  applyTerminology("Please use this grant where it’s needed most. Thank you!"),
  "This gift is made with gratitude and appreciation.",
  "Thank you for making a difference.",
  "Custom Note",
];

export const CONTRIBUTION_TYPE_KEYS = {
  WIRE_TRANSFER: "wire-transfer",
  PUBLIC_SECURITIES: "public-securities",
  EXTERNAL_FUND: "external-fund",
  IRA_TRANSFER: "ira-transfer",
  BANK_ACCOUNT_LINK: "bank-account-link",
  CREDIT_CARD: "credit-card",
  EXTERNAL_FUND_SECURITIES: "external-fund-securities",
  EXTERNAL_FUND_CASH: "external-fund-cash",
};

export const addFundsSingleStepOptions = [
  { id: CONTRIBUTION_TYPE_KEYS.BANK_ACCOUNT_LINK, label: "Bank Account" },
  { id: CONTRIBUTION_TYPE_KEYS.CREDIT_CARD, label: "Credit Card" },
];

const ALL_ADD_FUNDS_MULTIPLE_STEP_OPTIONS: AddFundsOption[] = [
  { id: CONTRIBUTION_TYPE_KEYS.WIRE_TRANSFER, label: "Wire Transfer" },
  { id: CONTRIBUTION_TYPE_KEYS.PUBLIC_SECURITIES, label: "Public Securities" },
  { id: CONTRIBUTION_TYPE_KEYS.EXTERNAL_FUND, label: "External Fund" },
  { id: CONTRIBUTION_TYPE_KEYS.IRA_TRANSFER, label: "QCD from IRA" },
  {
    id: CONTRIBUTION_TYPE_KEYS.EXTERNAL_FUND_CASH,
    label: "External Fund - Cash",
  },
  {
    id: CONTRIBUTION_TYPE_KEYS.EXTERNAL_FUND_SECURITIES,
    label: "External Fund - Securities",
  },
];

const getExternalFundIds = (
  isExternalSecuritiesEnabled: boolean,
  isIMADAF: boolean,
): string[] => {
  return isExternalSecuritiesEnabled && isIMADAF
    ? [
        CONTRIBUTION_TYPE_KEYS.EXTERNAL_FUND_CASH,
        CONTRIBUTION_TYPE_KEYS.EXTERNAL_FUND_SECURITIES,
      ]
    : [CONTRIBUTION_TYPE_KEYS.EXTERNAL_FUND];
};

export function getAddFundsMultipleStepOptions({
  programName,
  isSingleCharityFundAllowed,
  isIMADAF,
}: getAddFundsMultipleStepOptionsProps): AddFundsOption[] {
  const isExternalSecuritiesEnabled =
    process.env.NEXT_PUBLIC_ENABLE_EXTERNAL_SECURITIES === "true";

  const externalFundIds = getExternalFundIds(
    isExternalSecuritiesEnabled,
    isIMADAF,
  );

  if (programName === "AMK") {
    const amkIds = isSingleCharityFundAllowed
      ? [
          CONTRIBUTION_TYPE_KEYS.EXTERNAL_FUND_CASH,
          CONTRIBUTION_TYPE_KEYS.IRA_TRANSFER,
        ]
      : [
          CONTRIBUTION_TYPE_KEYS.EXTERNAL_FUND_CASH,
          CONTRIBUTION_TYPE_KEYS.EXTERNAL_FUND_SECURITIES,
        ];
    return ALL_ADD_FUNDS_MULTIPLE_STEP_OPTIONS.filter((o) =>
      amkIds.includes(o.id),
    );
  }

  const ids: string[] = [
    CONTRIBUTION_TYPE_KEYS.WIRE_TRANSFER,
    CONTRIBUTION_TYPE_KEYS.PUBLIC_SECURITIES,
    ...externalFundIds,
  ];

  if (isSingleCharityFundAllowed) {
    ids.push(CONTRIBUTION_TYPE_KEYS.IRA_TRANSFER);
  }

  return ALL_ADD_FUNDS_MULTIPLE_STEP_OPTIONS.filter((option) =>
    ids.includes(option.id),
  );
}

export const getCauseLabel = (causeKey: string): string => {
  const cause = CausesMap.find((c) => c.key === causeKey);
  return cause?.value ?? "";
};

export const replaceCauseKeysWithValues = (message: string): string => {
  let updatedMessage = message;

  CausesMap.forEach((cause) => {
    // Replace cause key with cause value (case-insensitive)
    // Handle underscores as either underscores or spaces
    const regex = new RegExp(cause.key.replace(/_/g, "[_\\s]"), "gi");
    updatedMessage = updatedMessage.replace(regex, cause.value);
  });

  return updatedMessage;
};

export const getCauseIcon = (
  causeKey: string,
  iconProps: React.SVGProps<SVGSVGElement> = {},
): React.ReactNode => {
  const cause = CausesMap.find((c) => c.key === causeKey);
  const Icon = cause?.icon;
  return Icon ? <Icon {...iconProps} /> : <></>;
};

export const CausesMap = [
  { key: "arts_and_culture", value: "Arts", icon: Arts },
  {
    key: "community_and_economic_development",
    value: "Community",
    icon: Development,
  },
  { key: "education", value: "Education", icon: Education },
  { key: "environment", value: "Environment", icon: Environment },
  { key: "health", value: "Health", icon: Health },
  { key: "sports_and_recreation", value: "Sports", icon: Sports },
  { key: "public_affairs", value: "Public Affairs", icon: Development },
  {
    key: "information_and_communications",
    value: "Information",
    icon: Media,
  },
  { key: "philanthropy", value: "Philanthropy", icon: Policy },
  {
    key: "public_safety_and_disaster_management",
    value: "Public safety",
    icon: Relief,
  },
  { key: "religion", value: "Religion", icon: Faith },
  {
    key: "international_human_rights",
    value: "Human rights",
    icon: Rights,
  },
  { key: "science", value: "Science", icon: Science },
  { key: "human_services", value: "Human Services", icon: Health },
  {
    key: "agriculture_fishing_and_forestry",
    value: "Agriculture",
    icon: Environment,
  },
  { key: "social_sciences", value: "Social Sciences", icon: Science },
  { key: "international_relations", value: "Relations", icon: Rights },
  { key: "human_rights", value: "Human rights", icon: Rights },
];

export const BREAKPOINTS = {
  md: 768, // Tailwind's default 'md' breakpoint in pixels
  lg: 1024, // Tailwind's default 'lg' breakpoint in pixels
};

export const REACT_QUERY_CACHE_KEYS_MAP = {
  RECURRING_GIFT: "recurringGift",
  ADVISOR_CAMPAIGNS: "advisor-campaigns",
  CAMPAIGNS_BY_ADVISOR: "campaigns-by-advisor",
  CLIENT_INFO: "client-info",
  FIRM_LISTS: "firm-lists",
  CUSTODIANS_LIST: "custodians-list",
  VALIDATE_UPLOADED_TICKERS: "validateTickerList",
  LIST_CONTRIBUTORS: "contributors",
  LIST_TICKERS: "searchTickers",
  CONTRIBUTION_GRANT_LIMITS: "listMinimumGrantsContributions",
  LIST_CAMPAIGNS: "campaigns",
  CAMPAIGN_DRAFTS: "campaignDrafts",
  LIST_FUND_SEARCH: "fundsearch",
  PUBLIC_CAMPAIGN_DETAILS: "publicCampaign",
  CAMPAIGN_DONATIONS: "campaignDonations",
  FAMILY_MEMBERS: "family-members",
  LIST_RECENT_CONTRIBUTIONS: "list-recent-contributions",
  ENGAGEMENT_DISTRIBUTION: "engagement-distribution",
  GET_USER: "get-user",
  RESTRICTED_CHARITIES: "restricted-charities",
  CURATES_LIST: "curates-list",
  CAUSES_LIST: "causes-list",
  RECENTLY_VIEWED_CHARITIES: "recentlyViewedCharities",
  RECOMMENDED_FOR_YOU: "recommendedFundsDonor",
  FAVORITE_CHARITIES: "favoriteCharities",
  FAVORITE_CHARITIES_INFINITE: "favoriteCharitiesInfinite",
  LIST_ADVISOR_CLIENTS: "list-advisor-clients",
  RECOMMENDED_FUNDS_FAMILY: "recommendedFundsFamily",
  SUGGESTED_FOR_YOU: "suggestedForYou",
  CHARITIES_BY_CAUSE: "charitiesByCause",
  LIST_RIA_CLIENTS: "list-ria-clients",
  LIST_INDIVIDUAL_DAFS: "list-individual-dafs",
  LIST_FIRM_USERS: "list-firm-users",
  FIRM_DETAILS: "firm-details",
  FIRM_DOCUMENTS: "firm-documents",
  DAFS_BY_EMAIL: "dafs-by-email",
  CHARITY_DETAILS: "charity-details",
  POOL_SET_RECOMMENDATIONS: "pool-set-recommendations",
  FIRM_POOLSETS: "firm-poolsets",
  CUSTOM_FEES: "custom-fees",
  CUSTOM_FEE_DETAILS: "custom-fee-details",
  INFINITE_TRANSACTIONS: "infinite-transactions",
  SCHEDULED_CONTRIBUTION_DETAILS: "scheduledContributionDetails",
  ACTIVE_CUSTODIAN_INTEGRATIONS: "active-custodian-integrations",
  LINKED_ACCOUNTS: "linked-accounts",
  CLIENTS_WITH_THIRD_PARTY_DAF: "clients-with-third-party-daf",
  TOP_RECOMMENDATIONS: "top-recommendations",
  BOOK_LEVEL_SETTINGS: "book-level-settings",
  SAVE_BOOK_LEVEL_SETTINGS: "save-book-level-settings",
  CLIENT_LEVEL_SETTINGS: "client-level-settings",
  SAVE_CLIENT_LEVEL_SETTINGS: "save-client-level-settings",
  GENERATE_TAX_OPTIMIZATION: "generate-tax-optimization",
  BUNCHING_PLAN_DRAFT: "bunching-plan-draft",
  CHARITY_MATCH: "charity-match",
  FETCH_ASSOCIATED_FAMILIES: "fetch-associated-families",
  SEARCH_ADVISOR_FAMILY_USERS: "search-advisor-family-users",
  IS_GIVE_USER: "is-give-user",
  GROWTH_ACTIVITIES: "growth-activities",
  GET_TRANSACTION_FILTERS: "get-transaction-filters",
  SEARCH_ALL: "search-all",
  ADVISOR_GROUPS: "advisor-groups",
  LIST_FIRM_ADVISORS: "list-firm-advisors",
  SEARCH_FIRM_ADVISORS: "search-firm-advisors",
  ADVISOR_GROUP_DETAILS: "advisor-group-details",
  ADVISOR_GROUP_CLIENTS: "advisor-group-clients",
  CAMPAIGN_METRICS: "campaign-metrics",
  CAMPAIGN_INFINITE: "campaign-infinite",
  CAMPAIGN_TEMPLATE_SETTINGS: "campaign-template-settings",
  PUBLIC_CAMPAIGN_DONATIONS: "public-campaign-donations",
  MS_OVERVIEW_STATS: "ms-overview-stats",
  MS_PROGRAM_AUM_STATS: "ms-program-aum-stats",
  REPORTS_OVERVIEW_STATS: "reports-overview-stats",
  REPORTS_PROGRAM_AUM_STATS: "reports-program-aum-stats",
  REPORTS_FIRMS_LIST: "reports-firms-list",
  REPORTS_FIRM_USERS: "reports-firm-users",
  REPORTS_RIA_CLIENTS: "reports-ria-clients",
  REPORTS_ADVISOR_GROUPS: "reports-advisor-groups",
  REPORTS_SEARCH_ADVISORS: "reports-search-advisors",
  REPORTS_ADVISOR_GROUP_CLIENTS: "reports-advisor-group-clients",
  REPORTS_ADVISOR_CLIENTS: "reports-advisor-clients",
  GROWTH_SUMMARY: "growth-summary",
  GROWTH_INSIGHTS: "growth-insights",
  GROWTH_UNFUNDED_DAFS: "growth-unfunded-dafs",
  GROWTH_ADVISORS: "growth-advisors",
  GROWTH_FLAGS: "growth-flags",
  ENTERPRISE_CLIENTS_LIST: "enterprise-clients-list",
  LIST_FIRM_ADMINS: "list-firm-admins",
  EMPLOYEES_SUMMARY: "employees-summary",
  FIRM_TAG_CATEGORIES: "firm-tag-categories",
  EMPLOYEE_DETAILS: "employee-details",
  GET_CORPORATE_DAF_FILTERS: "get-corporate-daf-filters",
  LIST_EMPLOYEES: "list-employees",
  LIST_PARTICIPANTS: "list-participants",
  EDIT_EMPLOYEE_DETAILS: "edit-employee-details",
  FEATURED_CHARITIES: "featured-charities",
  MS_INVEST_ALIGN_DATA: "ms-invest-align-data",
  MS_FUND_REALLOCATION_REBALANCE_REQUESTS:
    "ms-fund-reallocation-rebalance-requests",
  GET_DAF_POOLS_CACHE_KEY: "fetch-daf-pools",
  COMPANY_GRANTS: "company-grants",
  COMPANY_GRANTS_INFINITE: "company-grants-infinite",
  PUBLIC_SECURITY_BATCH_DETAILS: "publicSecurityBatchDetails",
  // Living Legacy — succession plan
  SUCCESSION_POLICY: "successionPolicy",
  SUNSET_CHARITY_ALLOCATIONS: "sunsetCharityAllocations",
  FAMILY_CHARITY_CAUSES: "familyCharityCauses",
  ALL_TIME_GRAPHS_DATA: "dashboard-graphs-data-alltime",
  GET_SUCCESSORS: "get-successors",
  // Living Legacy — transition console
  SUCCESSION_TRANSITION: "successionTransition",
  FAMILY_FREEZE_FLAGS: "familyFreezeFlags",
  PENDING_DEATH_TRANSITIONS: "pendingDeathTransitions",
  LIVING_LEGACY_TRANSITION_ALERTS: "livingLegacyTransitionAlerts",
  SUCCESSION_RECONCILIATIONS: "successionReconciliations",
  SUCCESSION_TRANSFERS: "successionTransfers",
  OPS_TRANSITIONS: "opsTransitions",
  OPS_TRANSITION: "opsTransition",
  OPS_ACTIVITY: "opsActivity",
  TRANSITION_ACTIVE_ITEMS: "transitionActiveItems",
  TRANSITION_ACTIVE_CAMPAIGNS: "transitionActiveCampaigns",
  TRANSITION_HISTORY: "transitionHistory",
  LIVING_LEGACY_PLANS: "livingLegacyPlans",
  LIVING_LEGACY_KPIS: "livingLegacyKpis",
};

// TODO: VK - Revisit this
export const US_STATES = [
  { label: "Alabama", value: "AL" },
  { label: "Alaska", value: "AK" },
  { label: "Arizona", value: "AZ" },
  { label: "Arkansas", value: "AR" },
  { label: "California", value: "CA" },
  { label: "Colorado", value: "CO" },
  { label: "Connecticut", value: "CT" },
  { label: "Delaware", value: "DE" },
  { label: "Florida", value: "FL" },
  { label: "Georgia", value: "GA" },
  { label: "Hawaii", value: "HI" },
  { label: "Idaho", value: "ID" },
  { label: "Illinois", value: "IL" },
  { label: "Indiana", value: "IN" },
  { label: "Iowa", value: "IA" },
  { label: "Kansas", value: "KS" },
  { label: "Kentucky", value: "KY" },
  { label: "Louisiana", value: "LA" },
  { label: "Maine", value: "ME" },
  { label: "Maryland", value: "MD" },
  { label: "Massachusetts", value: "MA" },
  { label: "Michigan", value: "MI" },
  { label: "Minnesota", value: "MN" },
  { label: "Mississippi", value: "MS" },
  { label: "Missouri", value: "MO" },
  { label: "Montana", value: "MT" },
  { label: "Nebraska", value: "NE" },
  { label: "Nevada", value: "NV" },
  { label: "New Hampshire", value: "NH" },
  { label: "New Jersey", value: "NJ" },
  { label: "New Mexico", value: "NM" },
  { label: "New York", value: "NY" },
  { label: "North Carolina", value: "NC" },
  { label: "North Dakota", value: "ND" },
  { label: "Ohio", value: "OH" },
  { label: "Oklahoma", value: "OK" },
  { label: "Oregon", value: "OR" },
  { label: "Pennsylvania", value: "PA" },
  { label: "Rhode Island", value: "RI" },
  { label: "South Carolina", value: "SC" },
  { label: "South Dakota", value: "SD" },
  { label: "Tennessee", value: "TN" },
  { label: "Texas", value: "TX" },
  { label: "Utah", value: "UT" },
  { label: "Vermont", value: "VT" },
  { label: "Virginia", value: "VA" },
  { label: "Washington", value: "WA" },
  { label: "West Virginia", value: "WV" },
  { label: "Wisconsin", value: "WI" },
  { label: "Wyoming", value: "WY" },
];

export const NOTIFICATION_OPTIONS = [
  {
    key: "contributions",
    label: "Notify me when contributions are made to my fund",
    disabled: false,
  },
  {
    key: "grants",
    label: applyTerminology("Notify me when grants are issued from my fund"),
    disabled: false,
  },
  {
    key: "familyMember",
    label: "Let me know when a family member joins",
    disabled: false,
  },
  {
    key: "receipts",
    label: applyTerminology("Send receipts for my grants or contributions"),
    disabled: true, // Cannot be turned off
    note: "Cannot be turned off" as string | undefined,
  },
] as const;

export type NotificationOption = (typeof NOTIFICATION_OPTIONS)[number];
export type NotificationKey = NotificationOption["key"];
export type TogglesState = Record<NotificationKey, boolean>;

// Map UI keys to API keys
export const notificationKeyToApiKey: Record<NotificationKey, string | null> = {
  contributions: "emailPrefDafContribution",
  grants: "emailPrefGifts",
  familyMember: "emailPrefOnboarding",
  receipts: null, // Not sent to API
};

export const DEFAULT_PAGE_SIZE = {
  FIRM_LISTS: 52,
  CHARITIES_BY_CAUSE: 24,
  LIST_RIA_CLIENTS: 20,
  LIST_FIRM_USERS: 20,
  FAMILY_INFO_TRANSACTIONS: 10,
  FAVORITE_CHARITIES: 12,
  ADVISOR_GROUPS: 10,
  ADVISOR_PICKER: 10,
  LIST_CAMPAIGNS_PAGES: 4,
  LIST_CAMPAIGNS_PAGE: 1,
};

export enum OrderByEnum {
  ASC = "ASC",
  DESC = "DESC",
}

export const DEFAULT_SUPPORT_EMAIL =
  process.env.NEXT_PUBLIC_SUPPORT_EMAIL || "care@tifingive.com";

export const DEFAULT_TECHNICAL_SUPPORT_URL =
  process.env.NEXT_PUBLIC_TECHNICAL_SUPPORT_URL ||
  "https://intercom.help/give-ria/en";

export const PROGRAM_NAME_MAP = (): string => {
  const programName = process.env.NEXT_PROGRAM_NAME ?? "TG";
  switch (programName) {
    case "TG":
      return "TIFIN Give";
    case "AMK":
      return "AssetMark Charitable";
    case "RBT":
      return "BlueGive";
    case "MSW":
      return "Morgan Stanley At Work";
    default:
      return programName;
  }
};
export const INTEGRATED_CUSTODIAN_TABLE_PAGE_SIZE = 4;

export const LINKED_ACCOUNTS_PAGE_SIZE = 10;

export const CLIENTS_WITH_THIRD_PARTY_DAF_PAGE_SIZE = 7;

export const SECURITIES_TABLE_PAGE_SIZE = 20;

export enum FILING_STATUS {
  SINGLE = "single",
  MARRIED_FILING_JOINTLY = "married_filing_jointly",
  MARRIED_FILING_SEPARATELY = "married_filing_separately",
  HEAD_OF_HOUSEHOLD = "head_of_household",
}

export enum ADJUST_INPUTS_MODE {
  BOOK_LEVEL = "bookLevel",
  CLIENT_LEVEL = "clientLevel",
}

export const POST_FUNDS_TO_MAP = (): string => {
  const programName = process.env.NEXT_PROGRAM_NAME ?? "TG";
  switch (programName) {
    case "TG":
      return "TIFIN Give";
    case "AMK":
      return "AssetMark Charitable Giving";
    case "RBT":
      return "BlueGive";
    case "MSW":
      return "Morgan Stanley At Work Giving";
    default:
      return "TIFIN Give";
  }
};

export const SUBMIT_GRANT_TO_MAP = (): string => {
  const programName = process.env.NEXT_PROGRAM_NAME ?? "TG";
  switch (programName) {
    case "TG":
      return "GiveClear Foundation";
    // TODO: what is the value for AMK, RBT, MSW?
    // case "AMK":
    //   return "GiveClear Foundation";
    // case "RBT":
    //   return "GiveClear Foundation";
    // case "MSW":
    //   return "GiveClear Foundation";
    default:
      return "GiveClear Foundation";
  }
};

export const IS_AMK_PROGRAM = (): boolean => {
  return process.env.NEXT_PROGRAM_NAME === "AMK";
};

export const IS_MSW_PROGRAM = (): boolean => {
  return process.env.NEXT_PROGRAM_NAME === "MSW";
};

export const IS_IMADAF = (disableAllocations: boolean = false): boolean => {
  return disableAllocations === true;
};

export function getExternalTransferSuccessMessage(): Record<string, string> {
  return {
    [CONTRIBUTION_TYPE_KEYS.EXTERNAL_FUND_CASH]:
      "Log into your external institution to initiate the cash transfer, using the details on this page.",
    [CONTRIBUTION_TYPE_KEYS.EXTERNAL_FUND]:
      "Log into your external institution to initiate the cash transfer, using the details on this page.",
    [CONTRIBUTION_TYPE_KEYS.EXTERNAL_FUND_SECURITIES]:
      "Log into your external institution to initiate the fund transfer using the details on this page.",
  };
}

export function getPublicSecuritiesSuccessMessage(): Record<string, string> {
  return {
    [CONTRIBUTION_TYPE_KEYS.EXTERNAL_FUND_SECURITIES]:
      "Log into your external institution to initiate the securities transfer, using the details on this page.",
    [CONTRIBUTION_TYPE_KEYS.PUBLIC_SECURITIES]:
      "Log into your external institution to initiate the fund transfer using the details on this page.",
  };
}

export const isTransferringPlatform = (selectedFundType: string): boolean => {
  return (
    isExternalFundSecurities(selectedFundType) ||
    selectedFundType === CONTRIBUTION_TYPE_KEYS.EXTERNAL_FUND_CASH ||
    selectedFundType === CONTRIBUTION_TYPE_KEYS.EXTERNAL_FUND ||
    selectedFundType === CONTRIBUTION_TYPE_KEYS.IRA_TRANSFER
  );
};

export const isExternalFundSecurities = (selectedFundType: string): boolean => {
  return selectedFundType === CONTRIBUTION_TYPE_KEYS.EXTERNAL_FUND_SECURITIES;
};

export const LEGAL_DOC_IDS = {
  FUND_PROGRAM_AGREEMENT: "fund-program-agreement",
  DAF_PROGRAM_GUIDELINES: "daf-program-guidelines",
  DAF_IPS: "daf-ips",
  PRIVACY_POLICY: "privacy-policy",
  SINGLE_CHARITY_FUND_PROGRAM_GUIDELINES:
    "single-charity-fund-program-guidelines",
  SINGLE_CHARITY_FUND_IPS: "single-charity-fund-ips",
  SUB_ADVISORY_FEES_AGREEMENT: "sub-advisory-fees-agreement",
  TERMS_AND_CONDITIONS: "terms-and-conditions",
} as const;

export const asset = (file: string): string =>
  `/api/documents/guidelines/${file}`;

export const getLegalDocsMap = (): { left: LegalDoc[]; right: LegalDoc[] } => {
  const programName = process.env.NEXT_PROGRAM_NAME ?? "TG";

  switch (programName) {
    case "RBT":
      return {
        left: [
          {
            id: LEGAL_DOC_IDS.DAF_PROGRAM_GUIDELINES,
            title: "Donor-Advised Fund Program Guidelines",
            href: asset("GiveClear_DAF_Program_Guidelines"),
            downloadable: true,
          },
          {
            id: LEGAL_DOC_IDS.DAF_IPS,
            title: "Investment Policy Statement (DAF)",
            href: asset("GiveClear_DAF_Investment_Policy_Statement"),
            downloadable: true,
          },
          {
            id: LEGAL_DOC_IDS.PRIVACY_POLICY,
            title: "Privacy Policy",
            href: "https://intercom.help/give-ria/en/articles/10092550-privacy-policy",
            downloadable: false,
          },
          {
            id: LEGAL_DOC_IDS.FUND_PROGRAM_AGREEMENT,
            title: "Fund Program Agreement",
            href: asset("Fund_Program_Agreement_Template"),
            downloadable: true,
          },
        ],
        right: [
          {
            id: LEGAL_DOC_IDS.SINGLE_CHARITY_FUND_PROGRAM_GUIDELINES,
            title: "Single Charity Fund Program Guidelines",
            href: asset("GiveClear_SCF_Program_Guidelines"),
            downloadable: true,
          },
          {
            id: LEGAL_DOC_IDS.SINGLE_CHARITY_FUND_IPS,
            title: "Investment Policy Statement (SCF)",
            href: asset("GiveClear_DAF_IPS_SCF"),
            downloadable: true,
          },
          {
            id: LEGAL_DOC_IDS.SUB_ADVISORY_FEES_AGREEMENT,
            title: "Sub-Advisory Fee Agreement",
            href: "https://intercom.help/give-ria/en/articles/11990668-sub-advisory-fee-terms-conditions",
            downloadable: true,
          },
          {
            id: LEGAL_DOC_IDS.TERMS_AND_CONDITIONS,
            title: "Terms & Conditions",
            href: "https://intercom.help/give-ria/en/articles/10092486-terms-of-use",
            downloadable: false,
          },
        ],
      };
    case "AMK":
      return {
        left: [
          {
            id: LEGAL_DOC_IDS.DAF_PROGRAM_GUIDELINES,
            title: "Donor-Advised Fund Program Guidelines",
            href: asset("GiveClear_DAF_Program_Guidelines"),
            downloadable: true,
          },
          {
            id: LEGAL_DOC_IDS.DAF_IPS,
            title: "Investment Policy Statement (DAF)",
            href: asset("GiveClear_DAF_Investment_Policy_Statement"),
            downloadable: true,
          },
          {
            id: LEGAL_DOC_IDS.PRIVACY_POLICY,
            title: "Privacy Policy",
            href: "https://intercom.help/give-ria/en/articles/10092550-privacy-policy",
            downloadable: false,
          },
          {
            id: LEGAL_DOC_IDS.FUND_PROGRAM_AGREEMENT,
            title: "Fund Program Agreement",
            href: asset("Fund_Program_Agreement_Template"),
            downloadable: true,
          },
        ],
        right: [
          {
            id: LEGAL_DOC_IDS.SINGLE_CHARITY_FUND_PROGRAM_GUIDELINES,
            title: "Single Charity Fund Program Guidelines",
            href: asset("GiveClear_SCF_Program_Guidelines"),
            downloadable: true,
          },
          {
            id: LEGAL_DOC_IDS.SINGLE_CHARITY_FUND_IPS,
            title: "Investment Policy Statement (SCF)",
            href: asset("GiveClear_DAF_IPS_SCF"),
            downloadable: true,
          },
          {
            id: LEGAL_DOC_IDS.SUB_ADVISORY_FEES_AGREEMENT,
            title: "Sub-Advisory Fee Agreement",
            href: "https://intercom.help/give-ria/en/articles/11990668-sub-advisory-fee-terms-conditions",
            downloadable: true,
          },
          {
            id: LEGAL_DOC_IDS.TERMS_AND_CONDITIONS,
            title: "Terms & Conditions",
            href: "https://intercom.help/give-ria/en/articles/10092486-terms-of-use",
            downloadable: false,
          },
        ],
      };

    default:
      return {
        left: [
          {
            id: LEGAL_DOC_IDS.DAF_PROGRAM_GUIDELINES,
            title: "Donor-Advised Fund Program Guidelines",
            href: asset("GiveClear_DAF_Program_Guidelines"),
            downloadable: true,
          },
          {
            id: LEGAL_DOC_IDS.DAF_IPS,
            title: "Investment Policy Statement (DAF)",
            href: asset("GiveClear_DAF_Investment_Policy_Statement"),
            downloadable: true,
          },
          {
            id: LEGAL_DOC_IDS.PRIVACY_POLICY,
            title: "Privacy Policy",
            href: "https://intercom.help/give-ria/en/articles/10092550-privacy-policy",
            downloadable: false,
          },
          {
            id: LEGAL_DOC_IDS.FUND_PROGRAM_AGREEMENT,
            title: "Fund Program Agreement",
            href: asset("Fund_Program_Agreement_Template"),
            downloadable: true,
          },
        ],
        right: [
          {
            id: LEGAL_DOC_IDS.SINGLE_CHARITY_FUND_PROGRAM_GUIDELINES,
            title: "Single Charity Fund Program Guidelines",
            href: asset("GiveClear_SCF_Program_Guidelines"),
            downloadable: true,
          },
          {
            id: LEGAL_DOC_IDS.SINGLE_CHARITY_FUND_IPS,
            title: "Investment Policy Statement (SCF)",
            href: asset("GiveClear_DAF_IPS_SCF"),
            downloadable: true,
          },
          {
            id: LEGAL_DOC_IDS.SUB_ADVISORY_FEES_AGREEMENT,
            title: "Sub-Advisory Fee Agreement",
            href: "https://intercom.help/give-ria/en/articles/11990668-sub-advisory-fee-terms-conditions",
            downloadable: true,
          },
          {
            id: LEGAL_DOC_IDS.TERMS_AND_CONDITIONS,
            title: "Terms & Conditions",
            href: "https://intercom.help/give-ria/en/articles/10092486-terms-of-use",
            downloadable: false,
          },
        ],
      };
  }
};

export const CAMPAIGN_STATES = {
  [FamilyCampaignStateChoices.Active]: "active",
  [FamilyCampaignStateChoices.Created]: "created",
  [FamilyCampaignStateChoices.Closed]: "closed",
  [FamilyCampaignStateChoices.Archived]: "archived",
};
export const getFeatureFlagFromFirmConfig = (
  firmConfig: FirmType["metaData"],
  featureFlagKey: string,
): boolean => {
  try {
    const parsedFirmMetadata =
      typeof firmConfig === "string"
        ? JSON.parse(firmConfig ?? "{}")
        : (firmConfig ?? {});
    const defaultFallback = defaultFallbackFeatureFlag(featureFlagKey);
    return (
      parsedFirmMetadata?.feature_flags?.[featureFlagKey] ?? defaultFallback
    );
  } catch {
    return defaultFallbackFeatureFlag(featureFlagKey);
  }
};

const FEATURE_FLAGS = {
  ENABLE_TO: process.env.NEXT_PUBLIC_ENABLE_TO === "true",
  ENABLE_SCF: process.env.NEXT_PUBLIC_ENABLE_SCF === "true",
  ENABLE_GG: process.env.NEXT_PUBLIC_ENABLE_GG === "true",
  ENABLE_SHOW_CHATBOT: process.env.NEXT_PUBLIC_ENABLE_SHOW_CHATBOT === "true",
  ENABLE_TRENDING_CAUSES_AND_CHARITIES:
    process.env.NEXT_PUBLIC_ENABLE_TRENDING_CAUSES_AND_CHARITIES === "true",
  ALL_CLIENTS_GROUP: process.env.NEXT_PUBLIC_ALL_CLIENTS_GROUP === "true",
  TAX_RECEIPT_WHITE_LABEL_ENABLED: false,
  gs_book_of_business_enabled: false,
  SHOW_INHERITANCE_REGISTRY: false,
  ENABLE_LIVING_LEGACY: false,
  // Per-firm only: never locked by or inherited from the enterprise
  ALLOW_DONOR_SSO: false,
  ENABLE_BULK_GRANT_SUBMISSION: false,
  // Sub-features of Tax Intelligence
  ENABLE_TAX_GAIN_HARVESTING: true,
  ENABLE_CONTRIBUTIONS_BUNCHING: false,
} as const;

export const FEATURE_FLAG_KEYS = Object.keys(FEATURE_FLAGS) as Array<
  keyof typeof FEATURE_FLAGS
>;

export const defaultFallbackFeatureFlag = (featureFlagKey: string): boolean => {
  return FEATURE_FLAGS[featureFlagKey as keyof typeof FEATURE_FLAGS] ?? false;
};

export const CC_TYPE = {
  REGULAR: "regular",
  CAMPAIGN: "campaign",
};
